import { useCallback } from "react";
import { writeXlsx } from "hucre/xlsx";

/**
 * Custom hook for exporting data to Excel with formatting using Hucre
 * @returns {Function} exportToExcel - Function to export data to Excel
 */
const useExcelExport = () => {
  /**
   * Export data to Excel with formatting
   * @param {Array} data - Array of objects to export
   * @param {Array} columns - Array of column definitions { field: 'fieldName', headerName: 'Display Name', width: 20 }
   * @param {string} fileName - Name of the Excel file (without extension)
   */
  const exportToExcel = useCallback(
    async (data, columns, fileName = "export") => {
      if (!data || data.length === 0) {
        return;
      }

      if (!columns || columns.length === 0) {
        return;
      }
      const border = {
        top: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      };
      const headerStyle = {
        font: { bold: true, size: 12 },
        alignment: { vertical: "center", horizontal: "left" },
        border,
      };

      const buffer = await writeXlsx({
        sheets: [
          {
            name: "Data",
            columns: columns.map((col) => ({
              header: col.headerName || col.field,
              key: col.field,
              width: col.width || 15,
            })),
            rows: [
              columns.map((col) => ({
                value: col.headerName || col.field,
                style: headerStyle,
              })),
              ...data.map((row) =>
                columns.map((col) => {
                  let value = row[col.field];

                  if (value === null || value === undefined) {
                    value = "";
                  } else if (col.type === "date" && value) {
                    value = new Date(value);
                  }

                  return {
                    value,
                    style: {
                      border,
                      alignment: { vertical: "center", horizontal: "left" },
                      ...(col.type === "date" && value
                        ? { numFmt: "dd-mm-yyyy" }
                        : {}),
                    },
                  };
                }),
              ),
            ],
          },
        ],
      });

      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const fullFileName = `${fileName}_${timestamp}.xlsx`;

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fullFileName;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    [],
  );

  return { exportToExcel };
};

export default useExcelExport;
